// projectWizard/steps/ProjectBasicInfo.jsx — Step 1
//
// Memoised: the wizard passes a stable `setField`, so this only re-renders
// when one of the values it actually reads changes.

import React from 'react';
import Box from '@mui/material/Box';
import { SectionCard, TextInput, ChipsInput, SelectInput, AdvancedPanel } from '../WizardControls';
import { STATUS_OPTIONS } from '../../ProjectFormSections';

const ProjectBasicInfo = ({ values, errors = {}, setField }) => (
  <SectionCard
    title="Basic Information"
    description="Start with the essentials. You can refine everything else later — nothing here is locked once saved."
  >
    <TextInput
      id="pf-name"
      label="Project name"
      required
      value={values.name}
      onChange={(v) => setField('name', v)}
      error={errors.name}
      hint="How this project appears everywhere students see it."
      placeholder="e.g. Real-Time Collaborative Whiteboard"
      maxLength={120}
    />

    <TextInput
      id="pf-summary"
      label="Summary"
      value={values.project_summary}
      onChange={(v) => setField('project_summary', v)}
      error={errors.project_summary}
      hint="One or two sentences shown on project cards and listings."
      placeholder="A short pitch a student can read in five seconds."
      multiline
      minRows={2}
      maxLength={280}
    />

    <TextInput
      id="pf-description"
      label="Description"
      value={values.description}
      onChange={(v) => setField('description', v)}
      error={errors.description}
      hint="The full brief — what gets built, and why it matters."
      placeholder="Describe the problem, the expected solution, and the scope."
      multiline
      minRows={5}
    />

    <TextInput
      id="pf-vision"
      label="Vision"
      value={values.vision}
      onChange={(v) => setField('vision', v)}
      error={errors.vision}
      hint="The outcome you want teams aiming at."
      placeholder="Where this project should take a team by the end."
      multiline
      minRows={3}
    />

    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2.5 }}>
      <ChipsInput
        id="pf-tags"
        label="Tags"
        value={values.tags}
        onChange={(v) => setField('tags', v)}
        error={errors.tags}
        hint="Type a tag and press Enter. Used for search and filtering."
        placeholder="realtime, websockets, collaboration"
      />
      <SelectInput
        id="pf-status"
        label="Status"
        value={values.status}
        onChange={(v) => setField('status', v)}
        options={STATUS_OPTIONS}
        error={errors.status}
        hint="Draft keeps it hidden from students."
      />
    </Box>

    <AdvancedPanel count={2}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
        <TextInput
          id="pf-category"
          label="Category"
          value={values.category}
          onChange={(v) => setField('category', v)}
          hint="Optional grouping label."
        />
        <TextInput
          id="pf-estimated-versions"
          label="Estimated versions"
          type="number"
          value={values.estimated_versions}
          onChange={(v) => setField('estimated_versions', v)}
          hint="How many iterations you expect this project to run."
        />
      </Box>
    </AdvancedPanel>
  </SectionCard>
);

export default React.memo(ProjectBasicInfo);
